import { getAuth, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { useShallow } from 'zustand/react/shallow';

import { useFetchRoles, useSyncGuardianProfile } from '@/features/auth/queries';
import { getFirebaseApp } from '@/libs/firebase/init.web';
import { captureException } from '@/libs/logger/logger-helpers';
import { ensureOnline } from '@/libs/network';
import { useSessionStore } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';

/**
 * Web variant of {@link useAppleLogin}. The native package
 * (`@invertase/react-native-apple-authentication`) has no web build, so on web
 * we sign in through Firebase's "Sign in with Apple" OAuth popup. Firebase uses
 * the Services ID + handler URL configured in the Firebase Console, so no
 * client-side client id is needed here.
 *
 * Apple only returns the user's name on the FIRST authorization; afterwards
 * Firebase exposes it via `displayName` (null on subsequent logins), so we
 * defensively split whatever is present and let the backend keep the existing
 * profile when the name is empty.
 *
 * Both `handleAppleLogin` and `handleAppleLoginAndroid` map to the same popup
 * flow so the consuming component can stay platform-agnostic.
 */
export const useAppleLogin = () => {
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const setIsPendingLoginSocial = useSessionStore(useShallow(s => s.setIsPendingLoginSocial));
  const { mutateAsync: syncGuardianProfile } = useSyncGuardianProfile();
  const { mutateAsync: fetchRoles } = useFetchRoles();

  const resolveRolesThenUnlock = async () => {
    const { roles } = await fetchRoles();
    useSessionStore.getState().setRoles(roles);
    useSessionStore.getState().setActiveRole(roles?.[0] ?? null);
    useSessionStore.getState().setSoftLoggedOut(false);
  };

  const handleAppleLogin = async () => {
    showGlobalLoading();
    setIsPendingLoginSocial(true);
    try {
      await ensureOnline();

      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');

      // Popup is user-driven, so no request timeout is applied here.
      const result = await signInWithPopup(getAuth(getFirebaseApp()), provider);

      const displayName = result.user.displayName?.trim() ?? '';
      const [firstname = '', ...rest] = displayName.split(/\s+/).filter(Boolean);
      const lastname = rest.join(' ');

      await syncGuardianProfile({
        data: {
          firstname,
          lastname,
        },
      });

      // Resolve THIS account's roles into the store BEFORE clearing the soft-logout lock.
      // Clearing the lock flips the withUnAuth gate and fires resetRootToPostAuthTarget, which
      // routes purely from the store's roles. Soft logout keeps the *previous* account warm, so
      // unlocking first would route from the old roles (a soft-logged-out coach signing in as a
      // guardian would land on the coach dashboard). Resolve, then unlock.
      await resolveRolesThenUnlock();
    } catch (e) {
      captureException(
        {
          error_payload: e,
        },
        { tags: { usk_log_type: 'apple_login_error' } },
      );
    } finally {
      hideGlobalLoading();
      setIsPendingLoginSocial(false);
    }
  };

  return {
    handleAppleLogin,
    handleAppleLoginAndroid: handleAppleLogin,
  };
};
